import type { FormikProps } from 'formik';
import React, { useRef } from 'react';

import { useParameterReferenceRange } from '@hooks/api/reference_range';

import type { FormValues } from '@models/reference-range';
import { TrackerType } from '@models/vital-sign';

import type {
  ReferenceRangeRiskLevel,
  ReferenceRangeRiskModel,
} from 'generated/types';

interface ParameterState {
  lowRef: React.RefObject<FormikProps<FormValues>> | null;
  normalRef: React.RefObject<FormikProps<FormValues>> | null;
  mildRef: React.RefObject<FormikProps<FormValues>> | null;
  moderateRef: React.RefObject<FormikProps<FormValues>> | null;
  highRef: React.RefObject<FormikProps<FormValues>> | null;
  crisisRef: React.RefObject<FormikProps<FormValues>> | null;
  belowTargetRef: React.RefObject<FormikProps<FormValues>> | null;
  targetRef: React.RefObject<FormikProps<FormValues>> | null;
  idealRef: React.RefObject<FormikProps<FormValues>> | null;
  isInitialized: boolean;
}

interface IContext {
  state: ParameterState;
  category: TrackerType;
  code: string;
  referenceRange: Map<ReferenceRangeRiskLevel, ReferenceRangeRiskModel>;
  refetchReferenceRange: () => Promise<void>;
}

const initialState: ParameterState = {
  lowRef: null,
  normalRef: null,
  mildRef: null,
  moderateRef: null,
  highRef: null,
  crisisRef: null,
  belowTargetRef: null,
  targetRef: null,
  idealRef: null,
  isInitialized: false,
};

const reducer = (state: ParameterState, updates: Partial<ParameterState>) => ({
  ...state,
  ...updates,
});

const Context = React.createContext<IContext>({
  state: initialState,
  category: TrackerType.VitalSigns,
  code: '',
  referenceRange: new Map(),
  refetchReferenceRange: () => Promise.resolve(),
});
Context.displayName = 'ParameterContext';

const Provider: React.FC<{
  category: TrackerType;
  children: React.ReactNode;
  code: string;
}> = ({ category, children = null, code }) => {
  const [state, dispatch] = React.useReducer(reducer, {
    ...initialState,
    lowRef: useRef(null),
    normalRef: useRef(null),
    mildRef: useRef(null),
    moderateRef: useRef(null),
    highRef: useRef(null),
    crisisRef: useRef(null),
    belowTargetRef: useRef(null),
    targetRef: useRef(null),
    idealRef: useRef(null),
  });

  const {
    data: referenceRange,
    loading,
    refetch,
    called,
  } = useParameterReferenceRange(code, category);
  const refRangeKey = JSON.stringify(Array.from(referenceRange.entries()));

  const refetchReferenceRange = async () => {
    await refetch();
  };

  React.useEffect(() => {
    if (!loading && called) {
      dispatch({ isInitialized: true });
    }
  }, [called, loading]);

  const value: IContext = React.useMemo(
    () => ({
      state,
      dispatch,
      category,
      code,
      referenceRange,
      refetchReferenceRange,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [category, code, state, refRangeKey, refetchReferenceRange]
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export {
  // Actions as ParameterActions,
  Context as ParameterContext,
  Provider as ParameterProvider,
};
export type { ParameterState, IContext as IParameterContext };
